<script>
import ConversationApi from 'dashboard/api/conversations';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';

const WA_GREEN = '#00a884';

const norm = s =>
  (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export default {
  name: 'ForwardDialog',
  props: {
    sourceConversationId: { type: [Number, String], required: true },
    messageId: { type: [Number, String], default: null },
    messageIds: { type: Array, default: null },
    onClose: { type: Function, default: () => {} },
    onSuccess: { type: Function, default: () => {} },
  },
  data() {
    return {
      waGreen: WA_GREEN,
      show: true,
      search: '',
      selectedInboxId: null,
      contacts: [],
      groups: [],
      selected: [],
      imgError: {},
      isLoading: false,
      isSubmitting: false,
    };
  },
  computed: {
    ...mapGetters({ inboxes: 'inboxes/getInboxes' }),
    idsToForward() {
      const ids =
        this.messageIds && this.messageIds.length
          ? [...this.messageIds]
          : this.messageId != null
            ? [this.messageId]
            : [];
      return ids.slice().sort((a, b) => Number(a) - Number(b));
    },
    apiInboxes() {
      return (this.inboxes || []).filter(i => i.channel_type === 'Channel::Api');
    },
    query() {
      return norm(this.search);
    },
    digitsQuery() {
      return this.search.replace(/\D/g, '');
    },
    numberSuggestion() {
      const raw = this.search.trim();
      if (!/^\+?[\d\s()-]{8,}$/.test(raw)) return null;
      if (this.digitsQuery.length < 8) return null;
      return { key: `n:${this.digitsQuery}`, phone: this.digitsQuery };
    },
    filteredGroups() {
      const q = this.query;
      const list = q
        ? this.groups.filter(g => norm(g.name).includes(q))
        : this.groups;
      return list.slice(0, 100);
    },
    filteredContacts() {
      const q = this.query;
      const digits = this.digitsQuery;
      const list = q
        ? this.contacts.filter(
            c =>
              norm(c.name).includes(q) ||
              (digits.length > 0 && (c.phone || '').includes(digits))
          )
        : this.contacts;
      return list.slice(0, 80);
    },
    nothingFound() {
      return (
        !this.isLoading &&
        !this.filteredGroups.length &&
        !this.filteredContacts.length &&
        !this.numberSuggestion
      );
    },
  },
  mounted() {
    this.selectedInboxId = this.defaultInboxId();
    this.reload();
  },
  methods: {
    defaultInboxId() {
      const getter = this.$store.getters.getConversationById;
      const conv = getter ? getter(this.sourceConversationId) : null;
      if (conv && conv.inbox_id) return conv.inbox_id;
      return this.apiInboxes.length ? this.apiInboxes[0].id : null;
    },
    onSelectInbox(id) {
      if (id === this.selectedInboxId) return;
      this.selectedInboxId = id;
      this.selected = [];
      this.reload();
    },
    async reload() {
      if (!this.selectedInboxId) return;
      this.isLoading = true;
      this.contacts = [];
      this.groups = [];
      try {
        const [c, g] = await Promise.all([
          ConversationApi.fetchWhatsappContacts(this.selectedInboxId),
          ConversationApi.fetchWhatsappGroups(this.selectedInboxId),
        ]);
        this.contacts = c.data.contacts || [];
        this.groups = g.data.groups || [];
      } catch (error) {
        this.contacts = [];
        this.groups = [];
      } finally {
        this.isLoading = false;
      }
    },
    initials(name) {
      const parts = (name || '').trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return '#';
      return (parts[0][0] + (parts[1] ? parts[1][0] : ''))
        .toUpperCase()
        .slice(0, 2);
    },
    isSelected(key) {
      return this.selected.some(s => s.key === key);
    },
    toggle(item) {
      const idx = this.selected.findIndex(s => s.key === item.key);
      if (idx >= 0) this.selected.splice(idx, 1);
      else this.selected.push(item);
    },
    toggleContact(c) {
      this.toggle({ key: `c:${c.jid}`, type: 'contact', name: c.name, phone: c.phone });
    },
    toggleGroup(g) {
      this.toggle({ key: `g:${g.jid}`, type: 'group', name: g.name, jid: g.jid });
    },
    toggleNumber() {
      const n = this.numberSuggestion;
      if (!n) return;
      this.toggle({ key: n.key, type: 'number', name: `+${n.phone}`, phone: n.phone });
    },
    async send() {
      if (!this.selected.length || !this.selectedInboxId) return;
      const ids = this.idsToForward;
      if (!ids.length) return;
      this.isSubmitting = true;
      let ok = 0;
      for (const t of this.selected) {
        const payload =
          t.type === 'group'
            ? { target_inbox_id: this.selectedInboxId, group_jid: t.jid }
            : { target_inbox_id: this.selectedInboxId, phone_number: t.phone };
        for (const id of ids) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await ConversationApi.forwardMessage(
              this.sourceConversationId,
              id,
              payload
            );
            ok += 1;
          } catch (error) {
            // keep going; counted below
          }
        }
      }
      this.isSubmitting = false;
      useAlert(
        ok > 0
          ? this.$t('CONVERSATION.CONTEXT_MENU.FORWARD.SUCCESS')
          : this.$t('CONVERSATION.CONTEXT_MENU.FORWARD.ERROR')
      );
      if (ok > 0) {
        this.onSuccess();
        this.close();
      }
    },
    close() {
      this.show = false;
      this.onClose();
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="show" :on-close="close">
    <div class="flex flex-col w-full" style="max-height: 78vh">
      <!-- header -->
      <div class="px-5 pt-5 pb-2">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD.FORWARD_TO') }}
        </h3>
        <div v-if="apiInboxes.length > 1" class="flex flex-wrap gap-1.5 mt-2">
          <button
            v-for="i in apiInboxes"
            :key="i.id"
            type="button"
            class="px-2.5 py-1 text-xs rounded-full"
            :style="
              selectedInboxId === i.id
                ? `background:${waGreen};color:#fff`
                : ''
            "
            :class="
              selectedInboxId === i.id ? '' : 'bg-n-slate-3 text-n-slate-11'
            "
            @click="onSelectInbox(i.id)"
          >
            {{ i.name }}
          </button>
        </div>
      </div>

      <!-- search -->
      <div class="px-5 pb-2">
        <div
          class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-slate-3"
        >
          <span class="w-4 h-4 i-lucide-search text-n-slate-10" />
          <input
            v-model="search"
            type="text"
            class="flex-1 text-sm bg-transparent outline-none text-n-slate-12"
            :placeholder="
              $t('CONVERSATION.CONTEXT_MENU.FORWARD.SEARCH_NAME_NUMBER')
            "
          />
        </div>
      </div>

      <!-- list -->
      <div class="flex-1 px-2 overflow-y-auto" style="min-height: 16rem">
        <p v-if="isLoading" class="p-4 text-sm text-center text-n-slate-10">
          {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD.LOADING') }}
        </p>

        <template v-else>
          <!-- selecionados (topo, para confirmar antes de enviar) -->
          <template v-if="selected.length">
            <div class="px-3 pt-3 pb-1 text-xs font-medium text-n-slate-10">
              {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD.SELECTED_SECTION') }}
            </div>
            <button
              v-for="s in selected"
              :key="`sel:${s.key}`"
              type="button"
              class="flex items-center w-full gap-3 px-3 py-2 rounded-lg hover:bg-n-slate-2"
              @click="toggle(s)"
            >
              <span
                class="flex items-center justify-center text-white rounded-full w-9 h-9 shrink-0"
                :style="`background:${waGreen}`"
              >
                <span
                  :class="
                    s.type === 'group'
                      ? 'w-4 h-4 i-lucide-users'
                      : 'w-4 h-4 i-lucide-user'
                  "
                />
              </span>
              <span class="flex-1 text-sm text-left truncate text-n-slate-12">
                {{ s.name }}
              </span>
              <span
                class="flex items-center justify-center w-5 h-5 rounded-full shrink-0"
                :style="`background:${waGreen};color:#fff`"
              >
                <span class="w-3 h-3 i-lucide-x" />
              </span>
            </button>
            <div class="mx-3 my-2 border-t border-n-weak" />
          </template>

          <!-- número avulso -->
          <button
            v-if="numberSuggestion"
            type="button"
            class="flex items-center w-full gap-3 px-3 py-2 rounded-lg hover:bg-n-slate-2"
            @click="toggleNumber"
          >
            <span
              class="flex items-center justify-center text-white rounded-full w-9 h-9"
              :style="`background:${waGreen}`"
            >
              <span class="w-4 h-4 i-lucide-phone" />
            </span>
            <span class="flex-1 text-sm text-left text-n-slate-12">
              {{
                $t('CONVERSATION.CONTEXT_MENU.FORWARD.SEND_TO_NUMBER', {
                  number: `+${numberSuggestion.phone}`,
                })
              }}
            </span>
            <span
              class="flex items-center justify-center w-5 h-5 rounded-full"
              :style="
                isSelected(numberSuggestion.key)
                  ? `background:${waGreen};color:#fff`
                  : 'border:1.5px solid var(--s-300)'
              "
            >
              <span
                v-if="isSelected(numberSuggestion.key)"
                class="w-3 h-3 i-lucide-check"
              />
            </span>
          </button>

          <!-- grupos -->
          <template v-if="filteredGroups.length">
            <div class="px-3 pt-3 pb-1 text-xs font-medium text-n-slate-10">
              {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD.GROUPS_SECTION') }}
            </div>
            <button
              v-for="g in filteredGroups"
              :key="`g:${g.jid}`"
              type="button"
              class="flex items-center w-full gap-3 px-3 py-2 rounded-lg hover:bg-n-slate-2"
              @click="toggleGroup(g)"
            >
              <span class="relative shrink-0">
                <img
                  v-if="g.avatar && !imgError[`g:${g.jid}`]"
                  :src="g.avatar"
                  class="object-cover rounded-full w-9 h-9"
                  @error="imgError[`g:${g.jid}`] = true"
                />
                <span
                  v-else
                  class="flex items-center justify-center rounded-full w-9 h-9 bg-n-slate-4 text-n-slate-11"
                >
                  <span class="w-4 h-4 i-lucide-users" />
                </span>
              </span>
              <span class="flex flex-col flex-1 min-w-0 text-left">
                <span class="text-sm truncate text-n-slate-12">{{ g.name }}</span>
                <span v-if="g.size" class="text-xs truncate text-n-slate-10">
                  {{
                    $t('CONVERSATION.CONTEXT_MENU.FORWARD.PARTICIPANTS', {
                      count: g.size,
                    })
                  }}
                </span>
              </span>
              <span
                class="flex items-center justify-center w-5 h-5 rounded-full shrink-0"
                :style="
                  isSelected(`g:${g.jid}`)
                    ? `background:${waGreen};color:#fff`
                    : 'border:1.5px solid var(--s-300)'
                "
              >
                <span
                  v-if="isSelected(`g:${g.jid}`)"
                  class="w-3 h-3 i-lucide-check"
                />
              </span>
            </button>
          </template>

          <!-- contatos -->
          <template v-if="filteredContacts.length">
            <div class="px-3 pt-3 pb-1 text-xs font-medium text-n-slate-10">
              {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD.CONTACTS_SECTION') }}
            </div>
            <button
              v-for="c in filteredContacts"
              :key="`c:${c.jid}`"
              type="button"
              class="flex items-center w-full gap-3 px-3 py-2 rounded-lg hover:bg-n-slate-2"
              @click="toggleContact(c)"
            >
              <span class="relative shrink-0">
                <img
                  v-if="c.avatar && !imgError[`c:${c.jid}`]"
                  :src="c.avatar"
                  class="object-cover rounded-full w-9 h-9"
                  @error="imgError[`c:${c.jid}`] = true"
                />
                <span
                  v-else
                  class="flex items-center justify-center text-xs font-medium rounded-full w-9 h-9 bg-n-slate-4 text-n-slate-11"
                >
                  {{ initials(c.name) }}
                </span>
              </span>
              <span class="flex flex-col flex-1 min-w-0 text-left">
                <span class="text-sm truncate text-n-slate-12">{{ c.name }}</span>
                <span class="text-xs truncate text-n-slate-10">{{ c.phone }}</span>
              </span>
              <span
                class="flex items-center justify-center w-5 h-5 rounded-full shrink-0"
                :style="
                  isSelected(`c:${c.jid}`)
                    ? `background:${waGreen};color:#fff`
                    : 'border:1.5px solid var(--s-300)'
                "
              >
                <span
                  v-if="isSelected(`c:${c.jid}`)"
                  class="w-3 h-3 i-lucide-check"
                />
              </span>
            </button>
          </template>

          <p
            v-if="nothingFound"
            class="p-4 text-sm text-center text-n-slate-10"
          >
            {{ $t('CONVERSATION.CONTEXT_MENU.FORWARD.NO_RESULTS') }}
          </p>
        </template>
      </div>

      <!-- footer send -->
      <div
        v-if="selected.length"
        class="flex items-center justify-between px-5 py-3 border-t border-n-weak"
      >
        <span class="text-sm text-n-slate-11">
          {{
            $t('CONVERSATION.CONTEXT_MENU.FORWARD.SELECTED_COUNT', {
              count: selected.length,
            })
          }}
        </span>
        <button
          type="button"
          :disabled="isSubmitting"
          class="flex items-center justify-center text-white rounded-full w-11 h-11 disabled:opacity-60"
          :style="`background:${waGreen}`"
          @click="send"
        >
          <span class="w-5 h-5 i-lucide-send" />
        </button>
      </div>
    </div>
  </woot-modal>
</template>
